import { ShowMoreComponent } from "@omni/components/components/Search/SearchResults/components/showMore/showMore.component";
import { useCallback, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import {
    clearFacetValues,
    selectFacetValue
} from "@/redux/modules/value/search/search.actions";
import { getSelectedValuesForFacet } from "@/redux/modules/value/search/search.selectors";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";
import { FacetOperator } from "@/utils/search/types/searchConfiguration";

import { FacetComponentProps } from "../../@types/facet.types";
import { FacetType } from "../../@types/facetType.types";
import {
    FACET_DELIMITER,
    FACET_SHOWMORE_LIMIT,
    getOperator
} from "../../facet.configuration";
import { DefaultFacetComponent } from "../DefaultFacet/defaultFacet.component";
import {
    mapToFacetValueInMillimetres,
    mapToFacetValueWithDisplayValue,
    sortUnitRange
} from "./unitRange.helpers";

export type UnitRangeFacetDisplayValue = {
    label: string;
    count: number;
    lowerBoundMillimetres: number;
    refined: boolean;
    onRefine: () => void;
};

const UnitRangeFacet = ({
    facet,
    component,
    sendEvent
}: FacetComponentProps<typeof FacetType.UNIT_RANGE>) => {
    const [isShowingMore, setIsShowingMore] = useState(false);

    const dispatch = useThunkDispatch();

    const selectedValues = useSelector(
        getSelectedValuesForFacet(component, facet.name),
        shallowEqual
    );

    const operator = useMemo(() => getOperator(facet.name), [facet.name]);
    const invalidations = useMemo(
        () => getInvalidationsForComponent(component),
        [component]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const createOnRefine = useCallback(
        (refined: boolean, facetName: string, facetValue: string) => () => {
            dispatch(
                refined
                    ? clearFacetValues(
                          component,
                          facetName,
                          [facetValue],
                          invalidations
                      )
                    : selectFacetValue(
                          component,
                          {
                              name: facetName,
                              operator: operator
                          },
                          facetValue,
                          invalidations,
                          sendEvent
                      )
            );
        },
        [component, operator, invalidations, dispatch]
    );

    const facetValues = [
        ...facet.values,
        ...selectedValues
            .filter((v) => !facet.values.some((f) => f.value === v))
            .map((v) => {
                const [, unit, lowerBound, upperBound] =
                    v.split(FACET_DELIMITER);
                return {
                    unit: unit,
                    upperBound: parseFloat(lowerBound),
                    lowerBound: parseFloat(upperBound),
                    value: v,
                    nbHits: 0
                };
            })
    ];

    const unitRangeFacetDisplayValues: Array<UnitRangeFacetDisplayValue> =
        facetValues
            .map(mapToFacetValueInMillimetres)
            .map(mapToFacetValueWithDisplayValue)
            .map(({ value, displayValue, nbHits, lowerBound }) => {
                const refined = selectedValues.includes(value);

                return {
                    label: displayValue,
                    count: nbHits,
                    lowerBoundMillimetres: lowerBound,
                    refined: refined,
                    onRefine: createOnRefine(refined, facet.name, value)
                };
            })
            .sort(sortUnitRange)
            .slice(0, !isShowingMore ? FACET_SHOWMORE_LIMIT : undefined);

    const handleToggleShowMore = () => setIsShowingMore((current) => !current);
    const canShowMore =
        Object.keys(facet.values ?? {}).length > FACET_SHOWMORE_LIMIT;

    return (
        <>
            <DefaultFacetComponent
                values={unitRangeFacetDisplayValues}
                showCount={operator === FacetOperator.OR}
            />
            {canShowMore && (
                <ShowMoreComponent
                    showMore={handleToggleShowMore}
                    isShowingMore={isShowingMore}
                />
            )}
        </>
    );
};

export { UnitRangeFacet };
